import axios from 'axios';
import config from '../config';

const API_URL = config.API_URL;

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add interceptor to include the auth token in requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Prayer service for managing prayer times
const prayerService = {
  // Get prayer times for a group
  getPrayerTimes: async (groupId, startDate = null, endDate = null) => {
    try {
      const params = new URLSearchParams({ _t: Date.now().toString() });
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      console.log(`Fetching prayer times for group ${groupId}${startDate ? ` from ${startDate} to ${endDate}` : ''}`);
      const response = await api.get(`/groups/${groupId}/prayer-times?${params.toString()}`);
      console.log('Prayer times response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting prayer times:', error);
      throw error;
    }
  },

  // Get custom prayer type names for a group
  getPrayerTypeNames: async (groupId) => {
    try {
      console.log(`Fetching prayer type names for group ${groupId}`);
      // Add cache-busting timestamp to ensure fresh data
      const timestamp = Date.now();
      const response = await api.get(`/groups/${groupId}/prayer-type-names?_=${timestamp}`);
      console.log('Prayer type names response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting prayer type names:', error);
      throw error;
    }
  },

  // Update a custom prayer type name
  updatePrayerTypeName: async (groupId, prayerType, customName, dayOfWeek = null) => {
    try {
      console.log(`Updating prayer type name for group ${groupId}, type ${prayerType}, day ${dayOfWeek}:`, customName);
      const response = await api.put(`/groups/${groupId}/prayer-type-names/${prayerType}`, {
        customName: customName,
        dayOfWeek: dayOfWeek
      });
      console.log('Updated prayer type name:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating prayer type name:', error);
      throw error;
    }
  },

  // Reset prayer type name to default
  resetPrayerTypeName: async (groupId, prayerType, dayOfWeek = null) => {
    try {
      console.log(`Resetting prayer type name for group ${groupId}, type ${prayerType}, day ${dayOfWeek}`);
      const response = await api.delete(`/groups/${groupId}/prayer-type-names/${prayerType}`, {
        data: { dayOfWeek: dayOfWeek }
      });
      console.log('Reset prayer type name:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error resetting prayer type name:', error);
      throw error;
    }
  },

  // Get custom prayer cards for a group
  getCustomPrayerCards: async (groupId) => {
    try {
      console.log(`Fetching custom prayer cards for group ${groupId}`);
      const response = await api.get(`/groups/${groupId}/custom-prayer-cards`);
      console.log('Custom prayer cards response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting custom prayer cards:', error);
      throw error;
    }
  },

  // Create a new custom prayer card
  createCustomPrayerCard: async (groupId, cardData) => {
    try {
      console.log(`Creating custom prayer card for group ${groupId}:`, cardData);
      const response = await api.post(`/groups/${groupId}/custom-prayer-cards`, cardData);
      console.log('Created custom prayer card:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating custom prayer card:', error);
      throw error;
    }
  },

  // Update a custom prayer card
  updateCustomPrayerCard: async (groupId, cardId, cardData) => {
    try {
      console.log(`Updating custom prayer card ${cardId} for group ${groupId}:`, cardData);
      const response = await api.put(`/groups/${groupId}/custom-prayer-cards/${cardId}`, cardData);
      console.log('Updated custom prayer card:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating custom prayer card:', error);
      throw error;
    }
  },

  // Delete a custom prayer card
  deleteCustomPrayerCard: async (groupId, cardId) => {
    try {
      console.log(`Deleting custom prayer card ${cardId} for group ${groupId}`);
      const response = await api.delete(`/groups/${groupId}/custom-prayer-cards/${cardId}`);
      console.log('Deleted custom prayer card:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting custom prayer card:', error);
      throw error;
    }
  },

  // Create a new prayer time
  createPrayerTime: async (groupId, prayerTimeData) => {
    try {
      console.log(`Creating prayer time for group ${groupId}:`, prayerTimeData);
      const response = await api.post(`/groups/${groupId}/prayer-times`, prayerTimeData);
      console.log('Created prayer time:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating prayer time:', error);
      throw error;
    }
  },

  // Update a prayer time
  updatePrayerTime: async (groupId, prayerTimeId, prayerTimeData) => {
    try {
      console.log(`Updating prayer time ${prayerTimeId} for group ${groupId}:`, prayerTimeData);
      const response = await api.put(`/groups/${groupId}/prayer-times/${prayerTimeId}`, prayerTimeData);
      console.log('Updated prayer time:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating prayer time:', error);
      throw error;
    }
  },

  // Delete a prayer time
  deletePrayerTime: async (groupId, prayerTimeId) => {
    try {
      console.log(`Deleting prayer time ${prayerTimeId} for group ${groupId}`);
      const response = await api.delete(`/groups/${groupId}/prayer-times/${prayerTimeId}`);
      console.log('Deleted prayer time:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting prayer time:', error);
      throw error;
    }
  },

  // Bulk update prayer times for a specific day
  bulkUpdatePrayerTimes: async (groupId, bulkUpdateData) => {
    try {
      console.log(`Bulk updating prayer times for group ${groupId}:`, bulkUpdateData);
      const response = await api.post(`/groups/${groupId}/prayer-times/bulk`, bulkUpdateData);
      console.log('Bulk updated prayer times:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error bulk updating prayer times:', error);
      throw error;
    }
  },

  // Get prayer time overrides for a specific date or date range
  getPrayerTimeOverrides: async (groupId, startDate, endDate = null) => {
    try {
      const params = new URLSearchParams({ startDate });
      if (endDate) params.append('endDate', endDate);
      
      console.log(`Fetching prayer time overrides for group ${groupId} from ${startDate} to ${endDate || startDate}`);
      const response = await api.get(`/groups/${groupId}/prayer-time-overrides?${params}`);
      console.log('Prayer time overrides response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting prayer time overrides:', error);
      throw error;
    }
  },

  // Create a new prayer time override for a specific date
  createPrayerTimeOverride: async (groupId, overrideData) => {
    try {
      console.log(`Creating prayer time override for group ${groupId}:`, overrideData);
      const response = await api.post(`/groups/${groupId}/prayer-time-overrides`, overrideData);
      console.log('Created prayer time override:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating prayer time override:', error);
      throw error;
    }
  },

  // Update a prayer time override
  updatePrayerTimeOverride: async (groupId, overrideId, overrideData) => {
    try {
      console.log(`Updating prayer time override ${overrideId} for group ${groupId}:`, overrideData);
      const response = await api.put(`/groups/${groupId}/prayer-time-overrides/${overrideId}`, overrideData);
      console.log('Updated prayer time override:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating prayer time override:', error);
      throw error;
    }
  },

  // Delete a prayer time override
  deletePrayerTimeOverride: async (groupId, overrideId) => {
    try {
      console.log(`Deleting prayer time override ${overrideId} for group ${groupId}`);
      const response = await api.delete(`/groups/${groupId}/prayer-time-overrides/${overrideId}`);
      console.log('Deleted prayer time override:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting prayer time override:', error);
      throw error;
    }
  },

  // Delete all overrides for a specific date
  deleteAllOverridesForDate: async (groupId, specificDate) => {
    try {
      console.log(`Deleting all overrides for group ${groupId} on ${specificDate}`);
      const response = await api.delete(`/groups/${groupId}/prayer-time-overrides/date/${specificDate}`);
      console.log('Deleted all overrides for date:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting overrides for date:', error);
      throw error;
    }
  },

  // =====================================================
  // NEW AGENDA ITEMS API
  // =====================================================

  // Get all agenda items for a group
  getAgendaItems: async (groupId) => {
    try {
      console.log(`Fetching agenda items for group ${groupId}`);
      const response = await api.get(`/groups/${groupId}/agenda-items?_t=${Date.now()}`);
      console.log('Agenda items response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting agenda items:', error);
      throw error;
    }
  },

  // Create new agenda item
  createAgendaItem: async (groupId, itemData) => {
    try {
      console.log(`Creating agenda item for group ${groupId}:`, itemData);
      const response = await api.post(`/groups/${groupId}/agenda-items`, itemData);
      console.log('Created agenda item:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating agenda item:', error);
      throw error;
    }
  },

  // Update agenda item
  updateAgendaItem: async (groupId, itemId, itemData) => {
    try {
      console.log(`Updating agenda item ${itemId}:`, itemData);
      const response = await api.put(`/groups/${groupId}/agenda-items/${itemId}`, itemData);
      console.log('Updated agenda item:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating agenda item:', error);
      throw error;
    }
  },

    // Delete agenda item
  deleteAgendaItem: async (groupId, itemId) => {
    try {
      console.log(`Deleting agenda item ${itemId}`);
      const response = await api.delete(`/groups/${groupId}/agenda-items/${itemId}`);
      console.log('Deleted agenda item:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting agenda item:', error);
      throw error;
    }
  },

  // Bulk update agenda items order
  updateAgendaItemsOrder: async (groupId, items) => {
    try {
      console.log(`Updating order for ${items.length} agenda items in group ${groupId}`);
      const response = await api.post(`/groups/${groupId}/agenda-items/bulk-order`, { items });
      console.log('Updated agenda items order:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating agenda items order:', error);
      throw error;
    }
  },

  // =====================================================
  // AGENDA OVERRIDES (Date-Specific Items)
  // =====================================================

  // Get agenda overrides for a specific date
  getAgendaOverrides: async (groupId, date = null) => {
    try {
      // Add cache-busting timestamp
      const params = new URLSearchParams({ _t: Date.now().toString() });
      if (date) params.append('date', date);
      
      const url = `/groups/${groupId}/agenda-overrides?${params.toString()}`;
      console.log(`Fetching agenda overrides for group ${groupId}${date ? ` on ${date}` : ''}`);
      const response = await api.get(url);
      console.log('Agenda overrides response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting agenda overrides:', error);
      throw error;
    }
  },

  // Create date-specific override
  createAgendaOverride: async (groupId, overrideData) => {
    try {
      console.log(`Creating agenda override for group ${groupId}:`, overrideData);
      const response = await api.post(`/groups/${groupId}/agenda-overrides`, overrideData);
      console.log('Created agenda override:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating agenda override:', error);
      throw error;
    }
  },

  // Update date-specific override
  updateAgendaOverride: async (groupId, overrideId, overrideData) => {
    try {
      console.log(`Updating agenda override ${overrideId}:`, overrideData);
      const response = await api.put(`/groups/${groupId}/agenda-overrides/${overrideId}`, overrideData);
      console.log('Updated agenda override:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating agenda override:', error);
      throw error;
    }
  },

  // Delete date-specific override
  deleteAgendaOverride: async (groupId, overrideId) => {
    try {
      console.log(`Deleting agenda override ${overrideId}`);
      const response = await api.delete(`/groups/${groupId}/agenda-overrides/${overrideId}`);
      console.log('Deleted agenda override:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting agenda override:', error);
      throw error;
    }
  },

  // Bulk update agenda overrides order
  updateAgendaOverridesOrder: async (groupId, date, items) => {
    try {
      console.log(`Updating order for ${items.length} agenda overrides on ${date} in group ${groupId}`);
      const response = await api.post(`/groups/${groupId}/agenda-overrides/bulk-order`, { date, items });
      console.log('Updated agenda overrides order:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating agenda overrides order:', error);
      throw error;
    }
  },

  // =====================================================
  // AGENDA EXCLUSIONS (Remove Recurring from Specific Date)
  // =====================================================

  // Create exclusion (remove recurring item from specific date)
  createAgendaExclusion: async (groupId, exclusionData) => {
    try {
      console.log(`Creating agenda exclusion for group ${groupId}:`, exclusionData);
      const response = await api.post(`/groups/${groupId}/agenda-exclusions`, exclusionData);
      console.log('Created agenda exclusion:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating agenda exclusion:', error);
      throw error;
    }
  }
};

export default prayerService;
